import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, Select } from 'antd';
import React, { HTMLProps, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { accessLevelOptions, datasourceOptions, isValidDatasource, setObjectType } from '../../utils';
import { DatabaseDatasource, Datasource, FileSystemDatasource, MessageBrokerDatasource, ObjectStorageDatasource, RemoteDatasource } from '../../models/Datasource';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import classNames from 'classnames';
import ScriptEditor from '../common/ScriptEditor';
import { QueryDetails, VizClient } from '../../api/VizClient';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { Option } from '../../models/generics';
import { RedisVariables } from '../../api/RedisVariables';

type Props = {
  datasource: Datasource;
  onSubmit?: (datasource: Datasource) => void;
  onCancel?: React.MouseEventHandler<HTMLButtonElement>;
}

export default function DatasourceForm({ datasource, onSubmit, onCancel }: Props) {
  const [editingDatasource, setEditingDatasource] = useState(datasource);
  const [drivers, setDrivers] = useState<any>();
  const [driversOptions, setDriversOptions] = useState<Option[]>();
  const [validated, setValidated] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const datasourceLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "datasources"));
  const testDisabled = useMemo(() => !isValidDatasource(editingDatasource), [editingDatasource]);

  const initDatasourceDrivers = useCallback(() => {
    const [namespace, key] = window.__env.DATASOURCE_DRIVERS_REDIS_VARIABLE?.split(":") || ['system', 'datasourceDrivers'];

    RedisVariables.getRedisVariable(key, namespace)
      .then(
        ({ data }) => {
          const driversMap = JSON.parse(data.value);
          setDrivers(driversMap);
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast("datasource-drivers-error", message, `Datasource Drivers Error ${status ? `(${status})` : ""}`, "danger");
        }
      )
  }, [showToast]);

  useEffect(() => {
    setEditingDatasource(datasource);
    initDatasourceDrivers();
  }, [datasource, initDatasourceDrivers]);

  useEffect(() => {
    if (editingDatasource.type && drivers) setDriversOptions(drivers[editingDatasource.type]);
  }, [editingDatasource.type, drivers]);

  const handleFieldChange = useCallback((field: string, checkbox?: boolean, pure?: boolean) => (evt: any) => {
    const value = (["type", "accessLevel", "driverType"].includes(field) || pure) ? evt : checkbox ? evt.target.checked : evt.target.value;

    setEditingDatasource(curr => {
      const newValue = {
        ...curr,
        [field]: value,
      };
      if (field === "type") newValue.driverType = "";
      return newValue;
    });
  }, []);

  const handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.stopPropagation();
    evt.preventDefault();
    setValidated(true);

    if (form.checkValidity() === true && datasource.type) {
      onSubmit?.(editingDatasource);
    }
  }

  const testDatasource = useCallback(() => {
    showLoader();
    const body = new QueryDetails(editingDatasource);
    const promise = editingDatasource.type === 'messageBroker'
      ? VizClient.getTopics(body)
      : VizClient.getDatabases(body);

    promise.then(
      () => {
        hideLoader();
        showToast('connection-success', `Successfully connected to ${datasourceLabels.singular}`, "Connection Valid", 'success');
      },
      err => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('connection-error', message, `Connection Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }, [editingDatasource, datasourceLabels, hideLoader, showLoader, showToast]);

  const renderMessageBrokerFields = () => {
    return setObjectType<MessageBrokerDatasource>(editingDatasource) && (
      <>
        <Form.Group controlId="datasource-brokers" className="mb-2">
          <Form.Label>Brokers</Form.Label>
          <Form.Control placeholder={`${datasourceLabels.singular} Brokers *`} size="sm" value={editingDatasource.brokers} onChange={handleFieldChange("brokers")} required />
          <Form.Control.Feedback type="invalid">{datasourceLabels.singular} Brokers is required.</Form.Control.Feedback>
        </Form.Group>

        <Row className="gx-2">
          <Col md={6} className="mb-2">
            <Form.Group controlId="datasource-username">
              <Form.Label>Username</Form.Label>
              <Form.Control placeholder={`${datasourceLabels.singular} Username`} size="sm" value={editingDatasource.username || ""} onChange={handleFieldChange('username')} />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="datasource-password">
              <Form.Label>Password</Form.Label>
              <Form.Control placeholder={`${datasourceLabels.singular} Password`} size="sm" value={editingDatasource.password || ""} onChange={handleFieldChange('password')} />
            </Form.Group>
          </Col>
        </Row>

        <Row className="gx-2">
          <Col md={6} className="mb-2">
            <Form.Group controlId="datasource-cafile">
              <Form.Label>CA File</Form.Label>
              <Form.Control as="textarea" placeholder={`${datasourceLabels.singular} CA File`} size="sm" value={editingDatasource.cafile || ""} onChange={handleFieldChange('cafile')} />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="datasource-cert-file">
              <Form.Label>Cert File</Form.Label>
              <Form.Control as="textarea" placeholder={`${datasourceLabels.singular} Cert File`} size="sm" value={editingDatasource.certfile || ""} onChange={handleFieldChange('certfile')} />
            </Form.Group>
          </Col>

          <Col md={6} className="mb-2">
            <Form.Group controlId="datasource-keyfile">
              <Form.Label>Key File</Form.Label>
              <Form.Control as="textarea" placeholder={`${datasourceLabels.singular} Key File`} size="sm" value={editingDatasource.keyfile || ""} onChange={handleFieldChange('keyfile')} />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="datasource-key-server-config">
              <Form.Label>Key Server Config</Form.Label>
              <Form.Control as="textarea" placeholder={`${datasourceLabels.singular} Key Server Config`} size="sm" value={editingDatasource.keyServerConfig || ""} onChange={handleFieldChange('keyServerConfig')} />
            </Form.Group>
          </Col>

          <Disclaimer />
        </Row>
      </>
    )
  }

  const renderRemoteSourceFields = useCallback(() => {
    return setObjectType<RemoteDatasource>(editingDatasource) && (
      <>
        <Form.Group controlId="datasource-url" className="mb-2">
          <Form.Label>Url</Form.Label>
          <Form.Control placeholder={`${datasourceLabels.singular} URL *`} size="sm" value={editingDatasource.url} onChange={handleFieldChange('url')} required />
        </Form.Group>

        <Form.Group controlId="datasource-auth">
          <Form.Label>Authentication</Form.Label>
          <ScriptEditor value={editingDatasource.auth} onChange={handleFieldChange('auth', false, true)} />
        </Form.Group>
      </>
    )
  }, [datasourceLabels, editingDatasource, handleFieldChange]);

  const renderObjectStorageFields = () => {
    return setObjectType<ObjectStorageDatasource>(editingDatasource) && (
      <>
        <Row className="gx-2">
          <Col className="mb-2">
            <Form.Group controlId="datasource-host">
              <Form.Label>Host</Form.Label>
              <Form.Control placeholder={`${datasourceLabels.singular} Host *`} size="sm" value={editingDatasource.host} onChange={handleFieldChange('host')} required />
            </Form.Group>
          </Col>

          <Col xs={4} className="mb-2">
            <Form.Group controlId="datasource-port">
              <Form.Label>Port</Form.Label>
              <Form.Control placeholder="Port" size="sm" value={editingDatasource.port || ""} onChange={handleFieldChange('port')} type="number" />
            </Form.Group>
          </Col>
        </Row>

        <Row className="align-items-end gx-2 mb-2">
          <Col md={6}>
            <Form.Group controlId="datasource-minio-bucket">
              <Form.Label>Bucket</Form.Label>
              <Form.Control placeholder={`${datasourceLabels.singular} Bucket *`} size="sm" value={editingDatasource.bucket || ""} onChange={handleFieldChange('bucket')} required />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="datasource-secure">
              <Form.Check label="Secure" type="checkbox" checked={editingDatasource.secure} onChange={handleFieldChange('secure', true)} />
            </Form.Group>
          </Col>
        </Row>

        <Row className="gx-2 mb-2">
          <Col md={6}>
            <Form.Group controlId="datasource-minio-accessKey">
              <Form.Label>Access Key</Form.Label>
              <Form.Control placeholder={`${datasourceLabels.singular} Access Key *`} size="sm" value={editingDatasource.accessKey || ""} onChange={handleFieldChange('accessKey')} required />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="datasource-minio-secretKey">
              <Form.Label>Secret Key</Form.Label>
              <Form.Control placeholder={`${datasourceLabels.singular} Secret Key *`} size="sm" value={editingDatasource.secretKey || ""} onChange={handleFieldChange('secretKey')} required />
            </Form.Group>
          </Col>
        </Row>

        <Disclaimer className="mb-2" />

        <Row className="gx-2">
          <Col md={6}>
            <Form.Group controlId="datasource-minio-prefixPath" className="mb-2">
              <Form.Label>Prefix Path</Form.Label>
              <Form.Control placeholder={`${datasourceLabels.singular} Prefix Path`} size="sm" value={editingDatasource.prefixPath || ""} onChange={handleFieldChange('prefixPath')} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="datasource-minio-regionName">
              <Form.Label>Region Name</Form.Label>
              <Form.Control placeholder={`${datasourceLabels.singular} Region Name`} size="sm" value={editingDatasource.regionName || ""} onChange={handleFieldChange('regionName')} />
            </Form.Group>
          </Col>
        </Row>
      </>
    )
  }

  const renderDatabaseFields = () => {
    return setObjectType<DatabaseDatasource>(editingDatasource) && (
      <>
        <Row className="gx-2">
          <Col className="mb-2" md={4}>
            <Form.Group controlId="datasource-host">
              <Form.Label>Host</Form.Label>
              <Form.Control placeholder={`${datasourceLabels.singular} Host *`} size="sm" value={editingDatasource.host} onChange={handleFieldChange('host')} required />
            </Form.Group>
          </Col>

          <Col className="mb-2" md={4}>
            <Form.Group controlId="datasource-port">
              <Form.Label>Port</Form.Label>
              <Form.Control placeholder={`${datasourceLabels.singular} Port *`} size="sm" value={editingDatasource.port} onChange={handleFieldChange('port')} type="number" required />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group className="mb-2" controlId="datasource-database">
              <Form.Label>Database</Form.Label>
              <Form.Control placeholder={`${datasourceLabels.singular} Database *`} size="sm" value={editingDatasource.database || ""} onChange={handleFieldChange('database')} required />
            </Form.Group>
          </Col>
        </Row>

        <Row className="gx-2">
          <Col md={6} className="mb-2">
            <Form.Group controlId="datasource-user">
              <Form.Label>User</Form.Label>
              <Form.Control placeholder={`${datasourceLabels.singular} User *`} size="sm" value={editingDatasource.user || ""} onChange={handleFieldChange('user')} required />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group controlId="datasource-password">
              <Form.Label>Password</Form.Label>
              <Form.Control placeholder={`${datasourceLabels.singular} Password *`} size="sm" value={editingDatasource.password || ""} onChange={handleFieldChange('password')} required />
            </Form.Group>
          </Col>
        </Row>

        <Disclaimer className="mb-2" />

        <Form.Group className="mb-2" controlId="datasource-catalogue">
          <Form.Label>Catalogue</Form.Label>
          <Form.Control size="sm" placeholder="Catalogue" value={editingDatasource.catalogue || ""} onChange={handleFieldChange('catalogue')} />
        </Form.Group>

        <Row className="gx-2 mb-2">
          <Col className="mb-2">
            <Form.Group controlId="datasource-thrift-host">
              <Form.Label>Thrift Host</Form.Label>
              <Form.Control size="sm" placeholder="Thrift Host" value={editingDatasource.thriftHost || ""} onChange={handleFieldChange('thriftHost')} />
            </Form.Group>
          </Col>

          <Col xs={3} className="mb-2">
            <Form.Group controlId="datasource-thrift-port">
              <Form.Label>Thrift Port</Form.Label>
              <Form.Control size="sm" type="number" placeholder="Thrift Port" value={editingDatasource.thriftPort || ""} onChange={handleFieldChange('thriftPort')} />
            </Form.Group>
          </Col>
        </Row>
      </>
    )
  }

  const renderFileSystemFields = () => {
    return setObjectType<FileSystemDatasource>(editingDatasource) && (
      <Row className="gx-2">
        <Col md={4} className="mb-2">
          <Form.Group controlId="datasource-file-path">
            <Form.Label>File Path</Form.Label>
            <Form.Control placeholder={`${datasourceLabels.singular} File Path *`} size="sm" value={editingDatasource.path || ""} onChange={handleFieldChange('path')} required />
          </Form.Group>
        </Col>

        <Col md={4} className="mb-2">
          <Form.Group controlId="datasource-file-name" className="mb-2">
            <Form.Label>File Name</Form.Label>
            <Form.Control placeholder={`${datasourceLabels.singular} File Name *`} size="sm" value={editingDatasource.filename || ""} onChange={handleFieldChange('filename')} required />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group controlId="datasource-file-extension">
            <Form.Label>File Extension</Form.Label>
            <Form.Control placeholder={`${datasourceLabels.singular} File Extension *`} size="sm" value={editingDatasource.extension || ""} onChange={handleFieldChange('extension')} required />
          </Form.Group>
        </Col>
      </Row>
    )
  }

  return (
    <Form noValidate validated={validated} onSubmit={handleSubmit}>
      <Row className="gx-2 align-items-center">
        <Col md={6}>
          <Form.Group className="mb-2" controlId="datasource-name">
            <Form.Label>Name</Form.Label>
            <Form.Control size="sm" placeholder={`${datasourceLabels.singular} name *`} value={editingDatasource.name} onChange={handleFieldChange('name')} required />
            <Form.Control.Feedback type="invalid">{datasourceLabels.singular} Name is required.</Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="datasource-access-level" className="mb-2">
            <Form.Label>Access Level</Form.Label>
            <Select
              placeholder={`Select ${datasourceLabels.singular} access level`}
              value={editingDatasource.accessLevel}
              allowClear
              onChange={handleFieldChange('accessLevel')}
              className="w-100"
              status={validated && !editingDatasource.accessLevel ? 'error' : ''}
            >
              {accessLevelOptions
                .filter(option => window.__env.ALLOWED_ACCESS_LEVELS ? window.__env.ALLOWED_ACCESS_LEVELS.includes(option.value) : true)
                .map((option, index) => (
                  <Select.Option key={index} value={option.value} label={option.label}>
                    <div>
                      <FontAwesomeIcon icon={option.icon} fixedWidth />
                      <span className="ms-2">{option.label}</span>
                    </div>
                  </Select.Option>
                ))}
            </Select>
            {validated && !editingDatasource.accessLevel && <Form.Control.Feedback className="d-block" type="invalid">{datasourceLabels.singular} access level is required.</Form.Control.Feedback>}
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-2" controlId="datasource-description">
            <Form.Label>Description</Form.Label>
            <Form.Control size="sm" placeholder={`${datasourceLabels.singular} description`} as="textarea" rows={4} value={editingDatasource.description} onChange={handleFieldChange('description')} />
          </Form.Group>
        </Col>
      </Row>

      <Row className="gx-2 align-items-end">
        <Col md={4} className="mb-2">
          <Form.Group controlId="datasource-type">
            <Form.Label>Type</Form.Label>
            <Select
              className="w-100"
              placeholder={`${datasourceLabels.singular} Type`}
              onChange={handleFieldChange("type")}
              value={editingDatasource.type}
            >
              {datasourceOptions.map((option, index) => (
                <Select.Option key={index} value={option.value}>
                  <FontAwesomeIcon icon={option.icon} fixedWidth />
                  <span className="ms-2">{option.label}</span>
                </Select.Option>
              ))}
            </Select>
          </Form.Group>
        </Col>

        <Col md={4} className="mb-2">
          <Form.Group controlId="datasource-driver">
            <Form.Label>Driver</Form.Label>
            <Select
              className="w-100"
              value={editingDatasource.driverType || undefined}
              allowClear
              onChange={handleFieldChange('driverType')}
              status={validated && !editingDatasource.driverType ? 'error' : ''}
              placeholder={`Select ${datasourceLabels.singular} Driver`}
              options={driversOptions || []}
            />
            {validated && !editingDatasource.driverType && <Form.Control.Feedback className="d-block" type="invalid">{datasourceLabels.singular} Driver is required.</Form.Control.Feedback>}
          </Form.Group>
        </Col>

        <Col md={4} className="mb-2">
          <Form.Group controlId="datasource-default">
            <Form.Check label="Default" type="checkbox" checked={editingDatasource.default} onChange={handleFieldChange('default', true)} />
          </Form.Group>
        </Col>
      </Row>

      {editingDatasource.type === 'objectStorage' && renderObjectStorageFields()}
      {editingDatasource.type === 'remote' && renderRemoteSourceFields()}
      {editingDatasource.type === 'messageBroker' && renderMessageBrokerFields()}
      {editingDatasource.type === 'database' && renderDatabaseFields()}
      {editingDatasource.type === 'fileSystem' && renderFileSystemFields()}

      <Flex justify="space-between" className="mt-3">
        <Button variant="light" size="sm" type="button" className="border px-3" onClick={onCancel}>
          <FontAwesomeIcon icon={solid('chevron-left')} />
          <span className="ms-2">Cancel</span>
        </Button>
        <Flex>
          <Button variant="light" size="sm" type="button" className="border me-3" onClick={testDatasource} disabled={testDisabled}>
            <FontAwesomeIcon icon={solid('signal')} />
            <span className="ms-2">Test Connection</span>
          </Button>
          <Button variant="success" size="sm" type="submit">
            <FontAwesomeIcon icon={solid('check')} />
            <span className="ms-2">Confirm</span>
          </Button>
        </Flex>
      </Flex>
    </Form>
  )
}

const Disclaimer = memo(({ className }: HTMLProps<HTMLDivElement>) => (
  <Alert variant="warning" className={classNames(className, "py-2 font-smaller")}>
    <FontAwesomeIcon icon={solid("warning")} />
    <span className="ms-2">Remember, personal credentials may be accessed by other users within the platform based on the datasource visibility.</span>
  </Alert>
))
